import { useLayoutEffect, useState, type RefObject } from 'react';

/**
 * 좌측 레일 **넘침 접기** — 그릴 수 있는 칸 수를 재고, 넘치는 만큼 «더보기» 한 칸으로 접는다.
 * (2026-08-26. 롤백은 `VITE_RAIL_OVERFLOW='0'` — layout-shell.ts 머리말에 근거를 적어 뒀다.)
 *
 * ── 왜 «넘치면 잘린다» 를 그냥 둘 수 없나 ────────────────────────────────────
 * 레일은 세로로 자라는데 화면 높이는 안 자란다. 넘친 뒤에는 `.navbar { overflow: hidden }` 이
 * **아래부터** 잘라내는데, 하필 `margin-top:auto` 로 아래에 밀려 있는 것이
 * 전체메뉴 · 조직도 · 통합설정이라 **그 셋이 먼저 사라진다**. 잘렸다는 표시도 없어서 쓰는 쪽은
 * 메뉴가 없어진 줄 안다.
 *
 * ── 재는 방법 ────────────────────────────────────────────────────────────
 * 항목을 다 그려 보고 넘친 만큼 걷어내면 한 프레임 깜빡인다. 그래서 **그리기 전에 셈한다** —
 * 필요한 값이 전부 레일의 붙박이(브랜드 · 전체메뉴 · 하단 목록)와 CSS 변수라 가능하다.
 *
 *   쓸 수 있는 높이 = 레일 안높이 − 붙박이 높이 합
 *   한 칸          = `--rail-box-h` + 목록의 row-gap
 *   칸 수          = ⌊(쓸 수 있는 높이 + gap) ÷ 한 칸⌋
 *
 * 붙박이 높이는 주 목록에 몇 개가 들어 있든 변하지 않으므로 **되먹임이 없다**(접었더니 칸이
 * 늘어 다시 펴지는 진동이 생기지 않는다). `margin-top:auto` 는 `parseFloat('auto') → NaN` 이라
 * 0 으로 떨어지는데, 그 자리는 원래 «남는 공간» 이라 0 으로 세는 것이 맞다.
 *
 * ⚠ 사다리(`--rail-box-h`)를 대신하지 않는다 — **먼저 압축하고, 그래도 넘치면 접는다**.
 *   순서가 뒤바뀌면 짧은 화면에서 접기부터 일어나 멀쩡히 들어갈 메뉴가 숨는다.
 */

/** CSS 길이 → px. `auto` · 빈 문자열 같은 비수치는 0 으로 본다(위 머리말 참조). */
function px(value: string): number {
  const n = Number.parseFloat(value);
  return Number.isFinite(n) ? n : 0;
}

/** 레일 한 칸의 기본 높이 — tokens.css `--rail-box-h` 의 기본값과 같은 값(변수 해석 실패 시 폴백). */
const RAIL_BOX_H_FALLBACK = 72;

/**
 * 지금 이 레일에 몇 칸을 그릴 수 있나. 잴 수 없으면 `null`(= 접지 않는다).
 *
 * `null` 을 돌려주는 자리가 중요하다 — jsdom 처럼 레이아웃이 없는 환경에서는 높이가 전부 0 이라
 * 억지로 세면 «한 칸» 이 나와 메뉴가 통째로 접힌다. 그래서 **안높이가 0 이하면 재지 않는다**
 * (기존 NavBar 테스트가 종전과 똑같이 돈다).
 */
export function measureRailCapacity(nav: HTMLElement, list: HTMLElement): number | null {
  if (!nav.isConnected) return null;
  const navCs = getComputedStyle(nav);
  const inner = nav.clientHeight - px(navCs.paddingTop) - px(navCs.paddingBottom);
  if (!(inner > 0)) return null;

  // 붙박이 = 주 목록을 뺀 나머지 형제 전부(브랜드 · 전체메뉴 목록 · 하단 목록).
  let reserved = 0;
  for (const child of Array.from(nav.children)) {
    if (child === list || !(child instanceof HTMLElement)) continue;
    const cs = getComputedStyle(child);
    if (cs.display === 'none') continue;
    reserved += child.offsetHeight + px(cs.marginTop) + px(cs.marginBottom);
  }

  const gap = px(getComputedStyle(list).rowGap);
  const boxH = px(getComputedStyle(document.documentElement).getPropertyValue('--rail-box-h'))
    || RAIL_BOX_H_FALLBACK;
  const slot = boxH + gap;
  if (!(slot > 0)) return null;

  // 마지막 칸은 gap 뒤에 오지 않으므로 한 번 더해 준다.
  return Math.max(1, Math.floor((inner - reserved + gap) / slot));
}

/**
 * 레일 용량을 재서 돌려준다. 창 크기 · 웹폰트 도착 · 붙박이 높이 변화에 따라 다시 잰다.
 * `enabled` 가 거짓이면 언제나 `null` 이라 호출부의 렌더 경로가 종전과 완전히 같아진다.
 */
export function useRailCapacity(
  navRef: RefObject<HTMLElement | null>,
  listRef: RefObject<HTMLElement | null>,
  enabled: boolean,
): number | null {
  const [capacity, setCapacity] = useState<number | null>(null);

  useLayoutEffect(() => {
    if (!enabled) {
      setCapacity((prev) => (prev === null ? prev : null));
      return;
    }
    const nav = navRef.current;
    const list = listRef.current;
    if (!nav || !list) return;

    const run = () => {
      const next = measureRailCapacity(nav, list);
      setCapacity((prev) => (prev === next ? prev : next));
    };
    run();

    // 레일 자체가 커지거나(창 세로 변화·상태바 토글) 붙박이가 커지면 다시 잰다.
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(run);
      ro.observe(nav);
      for (const child of Array.from(nav.children)) {
        if (child !== list && child instanceof HTMLElement) ro.observe(child);
      }
    }
    window.addEventListener('resize', run);
    // 웹폰트가 늦게 오면 라벨 줄높이가 바뀌어 붙박이 높이도 달라진다.
    const fonts = (document as Document & { fonts?: { ready?: Promise<unknown> } }).fonts;
    fonts?.ready?.then(run).catch(() => {});

    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', run);
    };
  }, [enabled, navRef, listRef]);

  return capacity;
}

/** 경로가 이 메뉴의 화면인가 — NavLink 기본 판정(정확히 같거나 하위 경로) + 별칭. */
export function isRailItemCurrent(
  pathname: string,
  to: string,
  aliasPrefix: Record<string, string[]> = {},
): boolean {
  const hit = (p: string) => pathname === p || pathname.startsWith(`${p}/`);
  if (hit(to)) return true;
  return (aliasPrefix[to] ?? []).some(hit);
}

export interface RailFold<T> {
  /** 레일에 그리는 것. */
  visible: T[];
  /** «더보기» 안에 담기는 것. 비어 있으면 «더보기» 칸 자체를 만들지 않는다. */
  folded: T[];
}

export interface FoldOptions<T> {
  /** 지금 보고 있는 화면. 절대 접지 않는다. */
  isCurrent: (item: T) => boolean;
  /**
   * 사용자가 별(★)로 고정해 둔 메뉴 (2026-08-26). 접힘 후보 중 **마지막까지 남는다**.
   *
   * 왜 순서를 바꾸지 않고 «우선순위» 로만 쓰나 — 메뉴 순서의 SSOT 는 관리 콘솔의
   * `admin_menu_registry` 다. 개인 고정이 그 순서를 앞뒤로 흔들면 «관리자가 정한 차례»와
   * «내 화면» 이 갈라진다. 그래서 **자리가 모자랄 때 누가 남느냐**에만 개입하고, 남은 것들의
   * 차례는 레지스트리 순서 그대로 둔다.
   */
  isPinned?: (item: T) => boolean;
}

/**
 * 용량에 맞춰 접는다. **순수 함수** — DOM 을 모른다(그래서 그대로 테스트할 수 있다).
 *
 * 규칙:
 *   1. 넘칠 때만 접는다. 다 들어가면 «더보기» 는 아예 없다.
 *   2. **마지막 한 칸은 «더보기» 몫**이다 — 그 칸도 자리를 차지하기 때문이다.
 *   3. 남는 자리는 **지금 보는 화면 → 고정한 메뉴 → 원래 차례** 순으로 채운다.
 *      통합설정을 쓰는 중에 통합설정이 사라지는 일이 없어진다.
 *   4. 고르고 난 뒤에는 **양쪽 다 원래 차례를 지킨다** — 메뉴가 화면에서 자리를 바꾸며
 *      돌아다니면, 자리를 외워서 누르는 사람이 매번 다시 찾아야 한다.
 */
export function foldRail<T>(
  items: T[],
  capacity: number | null,
  opts: FoldOptions<T>,
): RailFold<T> {
  if (capacity === null || capacity <= 0 || items.length <= capacity) {
    return { visible: items, folded: [] };
  }
  const room = Math.max(1, capacity - 1); // 규칙 2
  const { isCurrent, isPinned } = opts;
  // 자리를 다투는 기준 — 큰 것이 먼저 남고, 같으면 원래 차례가 이긴다(규칙 3).
  const rank = (item: T): number => (isCurrent(item) ? 2 : isPinned?.(item) ? 1 : 0);
  const keep = new Set(
    items
      .map((item, index) => ({ index, r: rank(item) }))
      .sort((a, b) => (b.r - a.r) || (a.index - b.index))
      .slice(0, room)
      .map((x) => x.index),
  );
  // 규칙 4 — 고른 결과를 원래 차례대로 다시 늘어놓는다.
  const visible: T[] = [];
  const folded: T[] = [];
  items.forEach((item, i) => { (keep.has(i) ? visible : folded).push(item); });
  return { visible, folded };
}
